import { Location } from '@angular/common';
import { AfterContentChecked, ChangeDetectorRef, Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ActivatedRoute, Params } from '@angular/router';
import { GenericResponse } from '@interfaces';
import { Subject, interval } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CancelContractService } from '@services/cancel-contract.service';
import { NewContractPymesService } from '@services/new-contract-pymes.service';
import { UtilService } from '@services/util.service';
import { ModalCancelContractComponent } from '@shared/modal-cancel-contract/modal-cancel-contract.component';
import { ToastrService } from '@shared/ui/toastr/toastr.service';
import { ContractDraft, ContractOrigin, RenovateContractDraft, RenovateContractPymes, RenovateContractSave, RenovateNextSaveData } from '../models/renovate-contract-pymes.model';
import { mapperContractFather, mapperFormToDraft, mapperFormToSave, mapperGetDraft, updateData } from '../services/renovate-contract-pymes.config';
import { RenovateContractPymesService } from '../services/renovate-contract-pymes.service';
import {
  RenovateContractPymesResumeComponent
} from '../components/renovate-contract-pymes-resume/renovate-contract-pymes-resume.component';
import { AuthService } from 'src/app/auth';

@Component({
  selector: 'app-renovate-contract-pymes',
  templateUrl: './renovate-contract-pymes.component.html',
  styleUrls: ['./renovate-contract-pymes.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class RenovateContractPymesComponent implements OnInit, AfterContentChecked, OnDestroy {
  public isLoading: boolean;
  public progress = 0;
  public tabIndex = 0;
  public goNext = false;
  public canSave = false;
  public dataContract: RenovateContractPymes;
  private contractSave: RenovateContractSave;
  public contractDraft: RenovateContractDraft;
  private firstTime = true;
  public fromPending = false;
  public idContract: string;
  public hasChangedDni = false;
  public hasChangedBankAccount = false;
  public hasSepaFile = true;
  public hasContractFile = true;
  private destroyed$ = new Subject<boolean>();
  private allowAsyncContract: boolean;
  public asyncContractRenovated = false;
  private hasCrmCode: string;
  private isUserSpanish = true;

  constructor(
    private location: Location,
    private activeRoute: ActivatedRoute,
    private renovateContractPymesService: RenovateContractPymesService,
    private utilService: UtilService,
    private cdRef: ChangeDetectorRef,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private newContractPymesService: NewContractPymesService,
    private cancelContractService: CancelContractService,
    private authService: AuthService,
  ) { }

  ngOnInit(): void {
    this.isUserSpanish = this.authService.getUserData().country === 'ES';
    this.allowAsyncContract = this.authService.getUserData().allowContratacionAsyncrona;
    this.renovateContractPymesService.resetLoader();
    this.newContractPymesService.resetSavedData();
    this.getLoader();
    this.getParamsUrl();
  }

  ngAfterContentChecked() {
    this.cdRef.detectChanges();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private getParamsUrl(): void {
    this.activeRoute.queryParams.pipe(takeUntil(this.destroyed$)).subscribe((res: Params) => {
      if (res.codeSocialReason) {
        this.asyncContractRenovated = true;
        this.newContractPymesService.setSavedData({
          codigoContrato: res.idContract,
          installation: {
            codigoRazonSocial: res.codeSocialReason
          },
          business: {
            nombre: res.codeSocialReason,
          },
          disabledAddSepa: res.disabledAddSepa,
          needFiles: true,
          hasChangedDni: res.hasChangedDni,
          hasChangedBankAccount: res.hasChangedBankAccount,
          bankAccountsSepa: null,
          disabledButtons: true
        });
        this.idContract = res.idContract;
        this.tabIndex = 1;
        this.progress = 100;
        this.fromPending = true;
      } else {
        if (res.idContract) {
          this.getContract(res.idContract);
          this.idContract = res.idContract;
        } else {
          this.firstTime = false;
          this.getDraftContract(parseInt(res.idDraft, 10));
        }
      }
    });
  }

  private getContract(idContract: string): void {
    this.renovateContractPymesService.setLoader(true);
    this.renovateContractPymesService.getContractRenovate(idContract).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse<ContractOrigin>) => {
        if (response.success) {
          this.dataContract = mapperContractFather(response.data, idContract, this.isUserSpanish);
          if (!this.isUserSpanish) {
            this.dataContract.campaign.consumptionEstimatedPeriod = [this.dataContract.campaign.estimateConsuption, null, null, null, null, null];
          }
        } else {
          this.utilService.toastErrors(response);
        }
        this.renovateContractPymesService.setLoader(false);
      },
      error => {
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private getDraftContract(idDraft: number): void {
    this.renovateContractPymesService.setLoader(true);
    this.renovateContractPymesService.getContractDraft(idDraft).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse<ContractDraft>) => {
        if (response.success) {
          this.dataContract = mapperGetDraft(response.data, idDraft);
          if (!this.isUserSpanish) {
            this.dataContract.campaign.consumptionEstimatedPeriod = [this.dataContract.campaign.estimateConsuption, null, null, null, null, null];
          }
          this.idContract = this.dataContract.contract.codContract;
        } else {
          this.utilService.toastErrors(response);
        }
        this.renovateContractPymesService.setLoader(false);
      },
      error => {
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  public handleGoBack(): void {
    this.location.back();
  }

  public changeLoader(loader): void {
    this.renovateContractPymesService.setLoader(loader);
  }

  private getLoader(): void {
    this.renovateContractPymesService.getLoader().pipe(takeUntil(this.destroyed$)).subscribe(
      value => {
        if (value > 0 && !this.isLoading) {
          this.isLoading = true;
        } else if (value === 0) {
          this.isLoading = false;
        }
      }
    );
  }

  public handleOpenProgress(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      disableClose: true,
      height: window.screen.height,
      width: '472px',
      position: {
        right: '0px'
      },
      data: this.dataContract
    };
    this.dialog.open(RenovateContractPymesResumeComponent, dialogConfig.data);
  }

  public handleGoNext(): void {
    if (this.isUserSpanish) {
      this.renovatecontract();
    } else {
      this.handleSave(true, true);
    }
  }

  private renovatecontract(): void {
    this.renovateContractPymesService.setLoader(true);
    if (this.allowAsyncContract) {
      this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_MESSAGE');
    }
    const apiCall = this.allowAsyncContract ?
      this.renovateContractPymesService.renovateContractPymesAsync(this.contractSave) :
      this.renovateContractPymesService.renovateContractPymes(this.contractSave);
    apiCall.pipe(takeUntil(this.destroyed$)).subscribe(
      response => {
        if (response.success) {
          if (this.allowAsyncContract) {
            this.asyncContractRenovated = false;
            const intervalSubscription = interval(10000).pipe(takeUntil(this.destroyed$)).subscribe(() => {
              if (!this.asyncContractRenovated) {
                this.checkRenovatedContractAsync(response.data?.identificadorTemporal);
              } else {
                intervalSubscription.unsubscribe();
              }
            });
          } else {
            this.renovateContractPymesSuccess(response);
            this.renovateContractPymesService.setLoader(false);
          }
        } else {
          this.utilService.toastErrors(response);
          this.renovateContractPymesService.setLoader(false);
        }
      },
      error => {
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private renovateContractPymesSuccess(response: GenericResponse): void {
    this.dataContract.contract.codContract = this.allowAsyncContract ? response.data.codigoCrmContrato : response.data.codContract;
    this.newContractPymesService.setSavedData({
      codigoContrato: this.dataContract.contract.codContract,
      installation: {
        codigoRazonSocial: this.dataContract.socialReason.name
      },
      business: {
        nombre: this.dataContract.socialReason.name
      },
      needFiles: false,
      hasChangedDni: response.data.hasChangedDni,
      hasChangedBankAccount: response.data.hasChangedBankAccount,
      bankAccountsSepa: null,
      disabledButtons: true
    });
    this.idContract = this.allowAsyncContract ? response.data.codigoCrmContrato : response.data.codContract;
    this.dataContract = updateData(this.dataContract, this.contractSave);
    if (!this.isUserSpanish) {
      this.dataContract.campaign.consumptionEstimatedPeriod = [this.dataContract.campaign.estimateConsuption, null, null, null, null, null];
    }
    this.tabIndex = 1;
    this.progress = 100;
  }

  private checkRenovatedContractAsync(identifier: string): void {
    this.newContractPymesService.checkContractAsync(identifier).pipe(takeUntil(this.destroyed$)).subscribe(
      res => {
        if (res.success) {
          if (res.data?.error) {
            if (res.data?.codigoCrmContrato) {
              this.renovateContractPymesSuccess(res);
            }
            this.checkRenovatedContractAsyncError(res.data?.descripcionError);
          } else {
            this.hasChangedDni = res.data?.hasChangedDni;
            this.hasChangedBankAccount = res.data?.hasChangedBankAccount;
            this.hasContractFile = res.data?.ficheroContrato;
            this.hasSepaFile = res.data?.ficheroSepa;
            if (res.data?.codigoCrmContrato && !this.hasCrmCode) {
              this.renovateContractPymesSuccess(res);
              this.renovateContractPymesService.setLoader(false);
              this.hasCrmCode = res.data.codigoCrmContrato;
            } else if (!res.data?.codigoCrmContrato && !this.hasCrmCode) {
              this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_MESSAGE');
            }
            if (res.data?.codigoCrmContrato && res.data?.ficheroContrato) {
              this.asyncContractRenovated = true;
            } else if (res.data?.codigoCrmContrato && !res.data?.ficheroContrato) {
              this.toastrService.success('NEW_CONTRACTS.ASYNC_CREATING_CONTRACT_FILES_MESSAGE');
            }
            this.cdRef.detectChanges();
          }
        } else {
          this.utilService.toastErrors(res);
          this.renovateContractPymesService.setLoader(false);
        }
      }, error => {
        this.renovateContractPymesService.setLoader(false);
        this.utilService.toastErrors(error);
      }
    );
  }

  private checkRenovatedContractAsyncError(errorMessage: string): void {
    this.renovateContractPymesService.setLoader(false);
    this.toastrService.error(errorMessage);
    this.asyncContractRenovated = true;
  }

  public handleCanSave(event: RenovateNextSaveData): void {
    this.goNext = event.canGoNext;
    this.canSave = event.canSave;
    this.contractDraft = mapperFormToDraft(this.dataContract, event.data);
    this.contractSave = mapperFormToSave(this.dataContract, event.data);
    if (this.firstTime && this.canSave) {
      this.firstTime = false;
      this.handleSave(true);
    }
  }

  public handleSave(hide: boolean, fromGoNext: boolean = false): void {
    this.renovateContractPymesService.setLoader(true);
    this.renovateContractPymesService.renovateContractDraftPymes(this.contractDraft.idBorrador, this.contractDraft)
      .pipe(takeUntil(this.destroyed$)).subscribe(
        response => {
          if (response.success) {
            if (!hide) {
              this.toastrService.error('NEW_CONTRACTS.DRAFT_SAVED');
            }
            if (!this.isUserSpanish && fromGoNext) {
              this.renovatecontract();
            }
          } else {
            this.utilService.toastErrors(response);
          }
          this.renovateContractPymesService.setLoader(false);
        },
        error => {
          this.renovateContractPymesService.setLoader(false);
          this.utilService.toastErrors(error);
        }
      );
  }

  public deleteContract(): void {
    const dialogConfig = new MatDialogConfig();
    dialogConfig.data = {
      autoFocus: false,
      height: '250px',
      width: '575px',
      disableClose: true
    };
    const dialogRef = this.dialog.open(ModalCancelContractComponent, dialogConfig);
    dialogRef.afterClosed().pipe(takeUntil(this.destroyed$)).subscribe((result: boolean) => {
      if (result) {
        this.renovateContractPymesService.setLoader(true);
        if (this.tabIndex === 0) {
          this.putDeleteDraft();
        } else {
          this.putDeleteContract();
        }
      }
    });
  }

  public putDeleteContract(): void {
    this.cancelContractService.cancelContract(this.idContract).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        this.deleteSuccess(response);
      },
      error => {
        this.deleteError(error);
      }
    );
  }

  public putDeleteDraft(): void {
    this.cancelContractService.cancelDraft(this.contractDraft.idBorrador).pipe(takeUntil(this.destroyed$)).subscribe(
      (response: GenericResponse) => {
        this.deleteSuccess(response);
      },
      error => {
        this.deleteError(error);
      }
    );
  }

  private deleteSuccess(response: GenericResponse): void {
    if (response.success) {
      this.toastrService.success('NEW_CONTRACTS.SUCCESS_CANCEL');
      this.handleGoBack();
    } else {
      this.utilService.toastErrors(response);
    }
    this.renovateContractPymesService.setLoader(false);
  }

  private deleteError(error): void {
    this.utilService.toastErrors(error);
    this.renovateContractPymesService.setLoader(false);
  }

}
